/**
 * Schedule Page - Staff Shift Management for Admin PWA
 * Weekly calendar view with shift management
 * Features: DRAFT → PUBLISHED workflow, Station assignment, Title field
 * 
 * REFACTORED: Components extracted to /components/schedule/
 */

import React, { useState, useEffect, useMemo } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { RefreshCw, Send } from 'lucide-react';
import { useBranch } from '../context/BranchContext';
import { toast } from 'sonner';

// Import refactored components
import { StatsCards, WeekCalendar, ShiftDialog } from '../components/schedule';
import { accountHeaders } from '@/lib/pwaAccount';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SchedulePage() {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  });
  
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  
  const [shiftForm, setShiftForm] = useState({
    employee_id: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    title: '',
    station: '',
    role: 'server',
    notes: ''
  });
  
  const { getBranchHeaders } = useBranch();
  
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    ...accountHeaders(),
    ...getBranchHeaders()
  });

  // Calculate week dates
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentWeekStart]);

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format date for API
  const formatDateAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Navigate weeks
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };

  // Fetch employees
  const fetchEmployees = useLatestCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/labor/employees`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  });

  // Fetch shifts for current week
  const fetchShifts = useLatestCallback(async () => {
    setLoading(true);
    try {
      const startDate = formatDateAPI(weekDates[0]);
      const endDate = formatDateAPI(weekDates[6]);
      
      const res = await fetch(
        `${API_URL}/api/labor/shifts?from=${startDate}&to=${endDate}${selectedEmployee !== 'all' ? `&employee_id=${selectedEmployee}` : ''}`,
        { headers: getHeaders() }
      );
      
      if (res.ok) {
        const data = await res.json();
        setShifts(data.shifts || []);
      }
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    if (weekDates.length > 0) {
      fetchShifts();
    }
  }, [currentWeekStart, selectedEmployee, weekDates.length, fetchShifts]);

  // Open dialog to add new shift
  const openAddDialog = (date) => {
    setEditingShift(null);
    setShiftForm({
      employee_id: selectedEmployee !== 'all' ? selectedEmployee : '',
      date: formatDateAPI(date),
      start_time: '09:00',
      end_time: '17:00',
      title: '',
      station: '',
      role: 'server',
      notes: ''
    });
    setDialogOpen(true);
  };

  // Open dialog to edit shift
  const openEditDialog = (shift) => {
    setEditingShift(shift);
    const startTime = shift.start_at ? shift.start_at.split('T')[1]?.substring(0, 5) : '09:00';
    const endTime = shift.end_at ? shift.end_at.split('T')[1]?.substring(0, 5) : '17:00';
    
    setShiftForm({
      employee_id: String(shift.employee_id),
      date: shift.start_at ? shift.start_at.split('T')[0] : '',
      start_time: startTime,
      end_time: endTime,
      title: shift.title || '',
      station: shift.station || '',
      role: shift.role_name || 'server',
      notes: shift.notes || ''
    });
    setDialogOpen(true);
  };

  // Save shift (create or update)
  const saveShift = async () => {
    if (!shiftForm.employee_id || !shiftForm.date) {
      toast.error('Please select an employee and date');
      return;
    }

    try {
      const startAt = `${shiftForm.date}T${shiftForm.start_time}:00`;
      const endAt = `${shiftForm.date}T${shiftForm.end_time}:00`;

      if (editingShift) {
        const res = await fetch(`${API_URL}/api/labor/shifts/${editingShift.id}`, {
          method: 'PATCH',
          headers: getHeaders(),
          body: JSON.stringify({
            start_at: startAt,
            end_at: endAt,
            title: shiftForm.title || null,
            station: (shiftForm.station && shiftForm.station !== 'none') ? shiftForm.station : null,
            notes: shiftForm.notes || null
          })
        });

        if (res.ok) {
          toast.success('Shift updated');
          setDialogOpen(false);
          fetchShifts();
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Failed to update shift');
        }
      } else {
        const res = await fetch(`${API_URL}/api/labor/shifts`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            employee_id: parseInt(shiftForm.employee_id),
            start_at: startAt,
            end_at: endAt,
            title: shiftForm.title || null,
            station: (shiftForm.station && shiftForm.station !== 'none') ? shiftForm.station : null,
            notes: shiftForm.notes || null,
            status: 'DRAFT'
          })
        });

        if (res.ok) {
          toast.success('Shift created as Draft');
          setDialogOpen(false);
          fetchShifts();
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Failed to create shift');
        }
      }
    } catch (err) {
      console.error('Failed to save shift:', err);
      toast.error('Failed to save shift');
    }
  };

  // Publish all drafts for the week
  const publishWeek = async () => {
    setPublishing(true);
    try {
      const fromDate = formatDateAPI(weekDates[0]);
      const toDate = formatDateAPI(weekDates[6]);
      
      const res = await fetch(`${API_URL}/api/labor/shifts/publish`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          from_date: fromDate,
          to_date: toDate
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Published ${data.published_count} shifts`);
        setPublishDialogOpen(false);
        fetchShifts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to publish shifts');
      }
    } catch (err) {
      console.error('Failed to publish:', err);
      toast.error('Failed to publish shifts');
    } finally {
      setPublishing(false);
    }
  };

  // Delete shift handlers
  const confirmDeleteShift = () => {
    setDialogOpen(false);
    setShiftToDelete(editingShift);
    setDeleteDialogOpen(true);
  };

  const deleteShift = async () => {
    if (!shiftToDelete) return;

    try {
      const res = await fetch(`${API_URL}/api/labor/shifts/${shiftToDelete.id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (res.ok) {
        toast.success('Shift deleted');
        setDeleteDialogOpen(false);
        setShiftToDelete(null);
        fetchShifts();
      }
    } catch (err) {
      console.error('Failed to delete shift:', err);
      toast.error('Failed to delete shift');
    }
  };

  // Calculate weekly stats
  const weeklyStats = useMemo(() => {
    let totalHours = 0;
    let totalShifts = shifts.length;
    let draftCount = 0;
    let publishedCount = 0;
    const employeeHours = {};

    shifts.forEach(shift => {
      const hours = (shift.duration_minutes || 0) / 60;
      totalHours += hours;
      
      if (shift.status === 'DRAFT') draftCount++;
      if (shift.status === 'PUBLISHED') publishedCount++;
      
      if (shift.employee_id) {
        employeeHours[shift.employee_id] = (employeeHours[shift.employee_id] || 0) + hours;
      }
    });

    return {
      totalShifts,
      totalHours: Math.round(totalHours * 10) / 10,
      employeeCount: Object.keys(employeeHours).length,
      draftCount,
      publishedCount
    };
  }, [shifts]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white" data-testid="schedule-title">Staff Schedule</h1>
          <p className="text-slate-400 text-sm mt-0.5 sm:mt-1">Manage staff shifts and schedules</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Publish Week Button */}
          {weeklyStats.draftCount > 0 && (
            <Button 
              onClick={() => setPublishDialogOpen(true)}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 h-8 sm:h-9 text-xs sm:text-sm"
              data-testid="publish-week-btn"
            >
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
              <span className="hidden xs:inline">Publish</span> ({weeklyStats.draftCount})
            </Button>
          )}
          
          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
            <SelectTrigger className="w-[120px] sm:w-[180px] bg-slate-800 border-slate-700 text-white h-8 sm:h-9 text-xs sm:text-sm" data-testid="employee-filter">
              <SelectValue placeholder="All Staff" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-white">All Staff</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.id} value={String(emp.id)} className="text-white">
                  {emp.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            size="icon"
            className="border-slate-700 h-8 w-8 sm:h-9 sm:w-9"
            onClick={() => fetchShifts()}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={weeklyStats} />

      {/* Week Calendar */}
      <WeekCalendar
        weekDates={weekDates}
        shifts={shifts}
        loading={loading}
        onPrevWeek={goToPreviousWeek}
        onNextWeek={goToNextWeek}
        onToday={goToCurrentWeek}
        onAddShift={openAddDialog}
        onEditShift={openEditDialog}
        formatDate={formatDate}
        formatDateAPI={formatDateAPI}
      />

      {/* Add/Edit Shift Dialog */}
      <ShiftDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingShift={editingShift}
        shiftForm={shiftForm}
        setShiftForm={setShiftForm}
        employees={employees}
        onSave={saveShift}
        onDelete={confirmDeleteShift}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Shift</DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 py-4 text-sm sm:text-base">
            Are you sure you want to delete this shift for <strong>{shiftToDelete?.employee_name}</strong>?
            This action cannot be undone.
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-slate-700 w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteShift}
              className="w-full sm:w-auto"
              data-testid="confirm-delete-btn"
            >
              Delete Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Send className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
              Publish Week Schedule
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300 text-sm sm:text-base">
              This will publish <strong className="text-emerald-400">{weeklyStats.draftCount} draft shifts</strong> for the week of{' '}
              <strong>{formatDate(weekDates[0])} - {formatDate(weekDates[6])}</strong>.
            </p>
            <p className="text-slate-400 text-xs sm:text-sm mt-2">
              Published shifts will be visible to staff members.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setPublishDialogOpen(false)}
              className="border-slate-700 w-full sm:w-auto"
              disabled={publishing}
            >
              Cancel
            </Button>
            <Button
              onClick={publishWeek}
              className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
              disabled={publishing}
              data-testid="confirm-publish-btn"
            >
              {publishing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Publish {weeklyStats.draftCount} Shifts
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
